import { useEffect, useMemo, useState } from 'react';
import { Button, Card, Form } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { getContestPluginConfig } from '../../services/contestPlugin';
import StandardModal from 'components/general/defaultModals/StandardModal';
import { getUserInitials, resolveAvatarSrc } from 'utils/avatar';

export default function ContestHubSetupModal({ meeting, agenda = [], show, onClose, isLocked = false, navigationParent = null }) {
  const { user: authUser } = useSelector((state) => state.auth);
  const url = process.env.REACT_APP_URL;
  const meetingId = meeting?.meetingDetails?.id || meeting?.meetingDetails?._id;
  const meetingContestTypes = useMemo(() => meeting?.meetingDetails?.contestTypes || [], [meeting?.meetingDetails?.contestTypes]);
  const [setup, setSetup] = useState({ contestTypes: [], contestJudges: [] });
  const [contestTypes, setContestTypes] = useState([]);
  const [instanceUsers, setInstanceUsers] = useState([]);
  const [pluginEnabled, setPluginEnabled] = useState(false);

  const candidates = useMemo(() => {
    const people = [];
    const allowedRoles = new Set(['officer', 'member', 'guest member', 'guest_member', 'guest-member']);
    const add = (person) => {
      const role = String(person?.acl || '').trim().toLowerCase();
      if (!allowedRoles.has(role)) return;
      const id = String(person?.id || person?._id || person?.tmAuthUserId || person?.user?._id || person?.user?.tmAuthUserId || '').trim();
      if (!id || people.some((item) => item.id === id)) return;
      const source = person?.user || person;
      const name = `${source?.firstName || ''} ${source?.lastName || ''}`.trim() || source?.email || id;
      people.push({
        id,
        name,
        avatarSrc: resolveAvatarSrc(source?.avatarUrl || source?.avatar || ''),
        initials: getUserInitials(source, name)
      });
    };
    instanceUsers.forEach(add);
    return people.sort((left, right) => left.name.localeCompare(right.name));
  }, [instanceUsers]);

  const normalizeContestJudges = useMemo(() => (contestJudgeEntries = []) => {
    const assignments = Array.isArray(contestJudgeEntries) ? contestJudgeEntries : [];
    return meetingContestTypes.map((contestType) => {
      const existing = assignments.find((entry) => entry?.contestType === contestType) || {};
      return {
        contestType,
        chiefJudge: existing.chiefJudge || '',
        tiebreakingJudge: existing.tiebreakingJudge || '',
        judges: Array.isArray(existing.judges) ? existing.judges : [],
        agendaItemIds: Array.isArray(existing.agendaItemIds) ? existing.agendaItemIds : []
      };
    });
  }, [meetingContestTypes]);

  const agendaEntries = useMemo(() => {
    if (Array.isArray(agenda) && agenda.length > 0) return agenda;
    if (Array.isArray(meeting?.items) && meeting.items.length > 0) return meeting.items;
    if (Array.isArray(meeting?.meetingDetails?.items) && meeting.meetingDetails.items.length > 0) return meeting.meetingDetails.items;
    if (Array.isArray(meeting?.meetingDetails?.agenda) && meeting.meetingDetails.agenda.length > 0) return meeting.meetingDetails.agenda;
    return [];
  }, [agenda, meeting?.items, meeting?.meetingDetails?.agenda, meeting?.meetingDetails?.items]);

  const agendaCandidates = useMemo(() => {
    const entries = Array.isArray(agendaEntries) ? agendaEntries : [];
    return entries
      .map((item, index) => {
        const explicitId = String(item?._id || item?.id || item?.agendaItemId || item?.itemId || '').trim();
        const fallbackId = `${meetingId || 'meeting'}:${index}:${String(item?.agendaEvent?._id || item?.agendaEvent?.name || 'agenda').trim()}`;
        const id = explicitId || fallbackId;
        if (!id) return null;
        const speechTitle = String(item?.item_title || '').split('\n').map((line) => line.trim()).find(Boolean) || '';
        const roleName = String(item?.meetingRole?.name || '').trim();
        const eventName = String(item?.agendaEvent?.name || '').trim();
        const speaker = `${item?.assignedUser?.firstName || ''} ${item?.assignedUser?.lastName || ''}`.trim();
        const primary = speechTitle || roleName || eventName || `Agenda item ${index + 1}`;
        const secondary = [roleName && roleName !== primary ? roleName : '', eventName && eventName !== primary ? eventName : '', speaker ? `Speaker: ${speaker}` : '']
          .filter(Boolean)
          .join(' | ');
        const label = secondary ? `${primary} (${secondary})` : primary;
        return {
          id,
          label,
          speakerName: speaker || 'TBA',
          avatarSrc: resolveAvatarSrc(item?.assignedUser?.avatarUrl || item?.assignedUser?.avatar || ''),
          initials: getUserInitials(item?.assignedUser, speaker),
          position: Number(item?.position ?? index)
        };
      })
      .filter(Boolean)
      .sort((left, right) => left.position - right.position)
      .map(({ id, label, speakerName, avatarSrc, initials }) => ({ id, label, speakerName, avatarSrc, initials }));
  }, [agendaEntries, meetingId]);

  useEffect(() => {
    if (!show || !authUser?.token || !meetingId) return;
    Promise.all([
      fetch(`${url}/contesthub/meetings/${meetingId}/setup`, { headers: { Authorization: `Bearer ${authUser.token}` } }).then((response) => response.ok ? response.json() : null),
      getContestPluginConfig(authUser.token),
      fetch(`${url}/users`, { headers: { Authorization: `Bearer ${authUser.token}` } }).then((response) => response.ok ? response.json() : [])
    ]).then(([saved, config, users]) => {
      const contestTypesForMeeting = saved?.contestTypes?.length ? saved.contestTypes : meetingContestTypes;
      const contestJudgeEntries = Array.isArray(saved?.contestJudges) ? saved.contestJudges : [];
      setSetup({
        contestTypes: contestTypesForMeeting,
        contestJudges: normalizeContestJudges(contestJudgeEntries)
      });
      setPluginEnabled(config.enabled);
      setContestTypes(config.contestTypes || []);
      setInstanceUsers(Array.isArray(users) ? users : []);
    }).catch(() => { setPluginEnabled(false); setInstanceUsers([]); });
  }, [authUser?.token, meetingContestTypes, meetingId, normalizeContestJudges, show, url]);

  async function save(patch) {
    const next = {
      contestTypes: meetingContestTypes.length ? meetingContestTypes : setup.contestTypes,
      contestJudges: Array.isArray(patch?.contestJudges) ? patch.contestJudges : setup.contestJudges,
      ...patch
    };
    setSetup(next);
    try {
      const response = await fetch(`${url}/contesthub/meetings/${meetingId}/setup`, { method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authUser.token}` }, body: JSON.stringify(next) });
      if (!response.ok) throw new Error('Unable to save ContestHub setup.');
      toast.success('ContestHub setup saved.', { position: 'bottom-left' });
    } catch (error) { toast.error(error.message, { position: 'bottom-left' }); }
  }

  function updateContestJudges(contestType, patch) {
    const nextContestJudges = setup.contestJudges.some((entry) => entry.contestType === contestType)
      ? setup.contestJudges.map((entry) => entry.contestType === contestType ? { ...entry, ...patch } : entry)
      : [...setup.contestJudges, { contestType, chiefJudge: '', tiebreakingJudge: '', judges: [], agendaItemIds: [], ...patch }];
    void save({ contestJudges: nextContestJudges });
  }

  function addJudge(contestType, value) {
    const target = setup.contestJudges.find((entry) => entry.contestType === contestType) || { contestType, chiefJudge: '', tiebreakingJudge: '', judges: [] };
    const judges = target.judges.includes(value) ? target.judges : [...target.judges, value];
    updateContestJudges(contestType, { judges });
  }

  function removeJudge(contestType, value) {
    const target = setup.contestJudges.find((entry) => entry.contestType === contestType) || { contestType, chiefJudge: '', tiebreakingJudge: '', judges: [] };
    updateContestJudges(contestType, { judges: target.judges.filter((judge) => judge !== value) });
  }

  function addAgendaItem(contestType, agendaItemId) {
    if (!agendaItemId) return;
    const target = setup.contestJudges.find((entry) => entry.contestType === contestType) || { contestType, chiefJudge: '', tiebreakingJudge: '', judges: [], agendaItemIds: [] };
    const agendaItemIds = Array.isArray(target.agendaItemIds) ? target.agendaItemIds : [];
    const nextAgendaItemIds = agendaItemIds.includes(agendaItemId) ? agendaItemIds : [...agendaItemIds, agendaItemId];
    updateContestJudges(contestType, { agendaItemIds: nextAgendaItemIds });
  }

  function removeAgendaItem(contestType, agendaItemId) {
    const target = setup.contestJudges.find((entry) => entry.contestType === contestType) || { contestType, chiefJudge: '', tiebreakingJudge: '', judges: [], agendaItemIds: [] };
    const agendaItemIds = Array.isArray(target.agendaItemIds) ? target.agendaItemIds : [];
    updateContestJudges(contestType, { agendaItemIds: agendaItemIds.filter((entry) => entry !== agendaItemId) });
  }

  if (!pluginEnabled) return null;

  const contestTypeNameFor = (contestType) => {
    const match = Array.isArray(contestTypes)
      ? contestTypes.find((type) => type?._id === contestType || type?.id === contestType)
      : null;
    return match?.name || contestType;
  };
  const nameFor = (id) => candidates.find((candidate) => candidate.id === id)?.name || id;
  const candidateFor = (id) => candidates.find((candidate) => candidate.id === id);
  const contestSetupEntries = (setup.contestJudges.length ? setup.contestJudges : setup.contestTypes.map((contestType) => ({ contestType, chiefJudge: '', tiebreakingJudge: '', judges: [], agendaItemIds: [] }))).filter((entry) => entry?.contestType);
  const agendaLabelFor = (id) => agendaCandidates.find((entry) => entry.id === id)?.label || id;
  const agendaCandidateFor = (id) => agendaCandidates.find((entry) => entry.id === id);
  const setupBody = <fieldset disabled={isLocked} className="stack contesthub-setup-modal-body" style={{ border: 'none', padding: 0, margin: 0 }}>
    {contestSetupEntries.length === 0 ? <p className="text-muted">No contest types are assigned to this meeting yet.</p> : contestSetupEntries.map((entry) => (
      <div key={entry.contestType} className="mb-3 border rounded p-3">
        <h5 className="mb-3">{contestTypeNameFor(entry.contestType)}</h5>
        <Card className="mb-3"><Card.Body><Form.Group><Form.Label>Agenda Items To Judge</Form.Label><Form.Select value="" onChange={(event) => { if (event.target.value) addAgendaItem(entry.contestType, event.target.value); event.target.value = ''; }}><option value="">Add agenda item</option>{agendaCandidates.filter((candidate) => !(entry.agendaItemIds || []).includes(candidate.id)).map((candidate) => <option key={candidate.id} value={candidate.id}>{candidate.label}</option>)}</Form.Select><div className="mt-2">{(entry.agendaItemIds || []).map((id) => { const candidate = agendaCandidateFor(id); return <Card className="mb-2" key={`${entry.contestType}-agenda-${id}`}><Card.Body className="d-flex align-items-center justify-content-between gap-3 py-2"><div className="d-flex align-items-center gap-2"><div className="contest-judge-avatar contesthub-agenda-item-avatar">{candidate?.avatarSrc ? <img src={candidate.avatarSrc} alt={`Avatar for ${candidate.speakerName}`} className="contest-judge-avatar-image" /> : candidate?.initials ? <span className="contest-judge-avatar-fallback">{candidate.initials}</span> : <i className="bi bi-person-fill contest-judge-avatar-fallback" aria-hidden="true" />}</div><span>{agendaLabelFor(id)}</span></div><Button size="sm" variant="danger" onClick={() => removeAgendaItem(entry.contestType, id)} aria-label={`Remove ${agendaLabelFor(id)}`} title={`Remove ${agendaLabelFor(id)}`}><i className="bi bi-trash" aria-hidden="true" /></Button></Card.Body></Card>; })}</div>{agendaCandidates.length === 0 && <small className="text-muted">No agenda items available for this meeting yet.</small>}</Form.Group></Card.Body></Card>
        <Card className="mb-3"><Card.Body><Form.Group><Form.Label>Chief Judge</Form.Label><Form.Select value={entry.chiefJudge || ''} onChange={(event) => updateContestJudges(entry.contestType, { chiefJudge: event.target.value })}><option value="">Select chief judge</option>{candidates.map((candidate) => <option key={candidate.id} value={candidate.id}>{candidate.name}</option>)}</Form.Select></Form.Group></Card.Body></Card>
        <Card className="mb-3"><Card.Body><Form.Group><Form.Label>Tiebreaking Judge</Form.Label><Form.Select value={entry.tiebreakingJudge || ''} onChange={(event) => updateContestJudges(entry.contestType, { tiebreakingJudge: event.target.value })}><option value="">Select tiebreaking judge</option>{candidates.map((candidate) => <option key={candidate.id} value={candidate.id}>{candidate.name}</option>)}</Form.Select></Form.Group></Card.Body></Card>
        <Card><Card.Body><Form.Group><Form.Label>Judges</Form.Label><Form.Select value="" onChange={(event) => { if (event.target.value) addJudge(entry.contestType, event.target.value); event.target.value = ''; }}><option value="">Add judge</option>{candidates.filter((candidate) => !entry.judges.includes(candidate.id)).map((candidate) => <option key={candidate.id} value={candidate.id}>{candidate.name}</option>)}</Form.Select><div className="mt-2">{(entry.judges || []).map((id) => { const candidate = candidateFor(id); return <Card className="mb-2" key={`${entry.contestType}-${id}`}><Card.Body className="d-flex align-items-center justify-content-between gap-3 py-2"><div className="d-flex align-items-center gap-2"><div className="contest-judge-avatar contesthub-agenda-item-avatar">{candidate?.avatarSrc ? <img src={candidate.avatarSrc} alt={`Avatar for ${candidate.name}`} className="contest-judge-avatar-image" /> : candidate?.initials ? <span className="contest-judge-avatar-fallback">{candidate.initials}</span> : <i className="bi bi-person-fill contest-judge-avatar-fallback" aria-hidden="true" />}</div><span>{nameFor(id)}</span></div><Button size="sm" variant="danger" onClick={() => removeJudge(entry.contestType, id)} aria-label={`Remove ${nameFor(id)}`} title={`Remove ${nameFor(id)}`}><i className="bi bi-trash" aria-hidden="true" /></Button></Card.Body></Card>; })}</div></Form.Group></Card.Body></Card>
      </div>
    ))}
    <small className="text-muted">Contestants and timing data will come from the agenda.</small>
  </fieldset>;

  return <StandardModal navigationParent={navigationParent} show={show} handleClose={onClose} title="Contest Setup" bodyClassName="contesthub-setup-modal" ModalBodyComponent={setupBody} ModalFooterComponent={<Button variant="secondary" onClick={onClose}>Close</Button>} />;
}
